/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/call/useCallRecordingPlayback.ts
 * PURPOSE: **`CallRecordingCard`** HTML5 audio — scrub sync, drag guard, play or pause, **`playbackRate`**
 * DEPENDENCIES: react
 * PUBLIC_API: useCallRecordingPlayback
 * SIDE_EFFECTS: **`audio.play()`** on user action
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 96/150
 * RECENT: 2026-05-07 [2.2.858] Extracted from **`CallRecordingCard`** — root **`CHANGELOG` [2.2.858]`**
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useCallRecordingPlayback(src: string | null, activityId: string) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const scrubDragRef = useRef(false);
  const [scrub, setScrub] = useState(0);
  const [rate, setRate] = useState(1);
  const [playing, setPlaying] = useState(false);

  const syncScrubFromAudio = useCallback(() => {
    const el = audioRef.current;
    if (!el || !src || scrubDragRef.current) return;
    const d = el.duration;
    if (!Number.isFinite(d) || d <= 0) return;
    setScrub(Math.min(100, Math.max(0, (el.currentTime / d) * 100)));
  }, [src]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !src) return;
    el.playbackRate = rate;
  }, [rate, src]);

  useEffect(() => {
    setScrub(0);
    setPlaying(false);
    scrubDragRef.current = false;
  }, [src, activityId]);

  const applyScrubPercent = useCallback(
    (pct: number) => {
      const clamped = Math.min(100, Math.max(0, pct));
      setScrub(clamped);
      const el = audioRef.current;
      if (!el || !src) return;
      const d = el.duration;
      if (!Number.isFinite(d) || d <= 0) return;
      el.currentTime = (clamped / 100) * d;
    },
    [src],
  );

  const togglePlay = useCallback(async () => {
    const el = audioRef.current;
    if (!el || !src) return;
    if (el.paused) {
      try {
        await el.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    } else {
      el.pause();
      setPlaying(false);
    }
  }, [src]);

  const audioEvents = useMemo(
    () => ({
      onEnded: () => {
        setPlaying(false);
        setScrub(0);
        const el = audioRef.current;
        if (el) el.currentTime = 0;
      },
      onPause: () => setPlaying(false),
      onPlay: () => setPlaying(true),
    }),
    [],
  );

  return {
    audioRef,
    scrubDragRef,
    scrub,
    rate,
    setRate,
    playing,
    syncScrubFromAudio,
    applyScrubPercent,
    togglePlay,
    audioEvents,
  };
}
